import { useState, useCallback } from "react";
import { getAllVendors } from "../../../../services/vendorApi";
import { useDropdownCache } from "../../../../shared/presentation/hooks/useDropdownCache";

export interface VendorListItem {
  id: number;
  zoho_vendorid: string;
  vendorid: string;
  vendor_legal_name: string;
  currency_id?: string;
  gst_treatment?: string;
  gstin?: string;
  state?: string;
  country?: string;
}

export interface UseVendorsForCreditNoteReturn {
  vendors: VendorListItem[];
  loading: boolean;
  error: string | null;
  fetchVendors: (
    searchTerm: string,
    limit: number,
    offset: number
  ) => Promise<{
    items: VendorListItem[];
    hasMore?: boolean;
  }>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for fetching vendors with pagination and caching
 * Supports SearchableDropdown fetchItems function signature
 */
export const useVendorsForCreditNote = (): UseVendorsForCreditNoteReturn => {
  const [vendors, setVendors] = useState<VendorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getCached, setCached } = useDropdownCache();

  /**
   * Fetch vendors with pagination and caching
   * Supports SearchableDropdown fetchItems function signature
   */
  const fetchVendors = useCallback(
    async (
      searchTerm: string,
      limit: number,
      offset: number
    ): Promise<{
      items: VendorListItem[];
      hasMore?: boolean;
    }> => {
      // Only cache the initial load (offset 0, no search)
      const shouldCache = offset === 0 && !searchTerm;
      const cacheKey = { searchTerm, limit, offset };

      if (shouldCache) {
        const cached = getCached<{
          items: VendorListItem[];
          hasMore?: boolean;
        }>("vendors", cacheKey);

        if (cached) {
          return cached;
        }
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getAllVendors({
          search: searchTerm || undefined,
          limit,
          offset,
        });

        // Map response to VendorListItem format
        const items: VendorListItem[] = (response.data || []).map(
          (vendor: any) => ({
            id: parseInt(vendor.vendorid) || 0,
            zoho_vendorid: vendor.zoho_vendorid || vendor.vendorid || "",
            vendorid: vendor.vendorid || "",
            vendor_legal_name: vendor.vendor_legal_name || "",
            currency_id: vendor.currency_id || "",
            gst_treatment: vendor.gst_treatment || "",
            gstin: vendor.gstin || "",
            state: vendor.state || "",
            country: vendor.country || "",
          })
        );

        const hasMore = response.pagination?.has_more || false;

        const result = {
          items,
          hasMore,
        };

        // Cache the result if it's the initial load
        if (shouldCache) {
          setCached("vendors", cacheKey, result);
        }

        return result;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to fetch vendors";
        setError(errorMessage);
        console.error("Failed to fetch vendors:", error);
        return {
          items: [],
          hasMore: false,
        };
      } finally {
        setLoading(false);
      }
    },
    [getCached, setCached]
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setVendors([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    vendors,
    loading,
    error,
    fetchVendors,
    clearError,
    reset,
  };
};
